import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { HttpStatusCode, isAxiosError } from 'axios';
import { PAYMENT_RECEIVED_SSE_URL } from 'common/constants';
import React, { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  setContractSignDate,
  setContractingKey,
  setContractingSelected,
  setContractingState,
  setTimeRemaining,
  setTokenValid,
} from 'v3/data/store/contracting/actions';
import { ContractingStateEnum } from 'v3/data/store/contracting/enums';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { setIntallmentContractingKey } from 'v3/data/store/installments/actions';
import { setPaymentData } from 'v3/data/store/payment/actions';
import { clearProposalInfo } from 'v3/data/store/proposal/actions';
import { useSse } from 'v3/hooks/useSse';
import { PersonalInformation } from 'v3/pages/Contracting/Details/Cards/PersonalInformation';
import { IContractingDetailsParams } from 'v3/pages/Contracting/Details/Cards/types';
import { ProposalInformation } from 'v3/pages/Payments/Details/Cards/ProposalInformations';
import { Container, Content, Header, InstallmentArea, MainTitle } from 'v3/pages/Payments/Details/styles';
import { GO_BACK } from 'v3/routes/enum';
import * as api from 'v3/services/api';
import { InstallmentTable } from 'v3/shared/components/Table/InstallmentTable';
import { InstallmentToPayTable } from 'v3/shared/components/Table/InstallmentsToPayTable';
import { CommonTemplate } from 'v3/shared/templates/CommonTemplate';
import { BodySmallBold, BodySmallRegular } from 'v3/shared/typography/BodySmall';
import { BodySmallBoldStyled } from 'v3/shared/typography/BodySmall/styles';
import { HeadlineMedium } from 'v3/shared/typography/HeadlineMedium';

export const PaymentsDetailsPage = () => {
  const navigate = useNavigate();
  const { contractingKey } = useParams<Readonly<IContractingDetailsParams>>() as IContractingDetailsParams;
  const { data, connect } = useSse(`${PAYMENT_RECEIVED_SSE_URL}${contractingKey}`, '');

  const handleNavigation = () => {
    navigate(GO_BACK);
  };

  const resetContractingFields = () => {
    setTokenValid(false);
    setTimeRemaining('');
    setContractSignDate('');
  };

  const handleUpdateStatus = useCallback(async () => {
    try {
      const response = await api.getContractingState(contractingKey);
      setContractingState(response.data.status);
      setTokenValid(response.data.tokenValid);
      setTimeRemaining(response.data.timeRemaining);
      setContractSignDate(response.data.contractSignDate);
    } catch (error) {
      if (isAxiosError(error)) {
        resetContractingFields();

        if (error.response?.status === HttpStatusCode.Locked) {
          setContractingState(ContractingStateEnum.CreditAnalysisRejected);
        }
        if (error.response?.status === HttpStatusCode.Gone) {
          setContractingState(ContractingStateEnum.ExpiredKey);
        }
      } else {
        showErrorDialog('Erro ao obter status da contratação', error);
        console.error(error);
      }
    }
  }, [contractingKey]);

  const handleGetContracting = useCallback(async () => {
    try {
      const contracting = await api.getContracting(contractingKey);
      setContractingSelected(contracting.data);
    } catch (error) {
      showErrorDialog('Erro ao obter contratação', error);
    }
  }, [contractingKey]);

  useEffect(() => {
    handleGetContracting();
    clearProposalInfo();
    handleUpdateStatus();
    setContractingKey(contractingKey);
    setIntallmentContractingKey(contractingKey);
  }, [handleUpdateStatus, handleGetContracting, contractingKey]);

  useEffect(() => {
    if (data) {
      setPaymentData(data);
    }
  }, [data]);

  useEffect(() => {
    connect();
  }, []);

  return (
    <CommonTemplate>
      <Container>
        <Content>
          <Header>
            <MainTitle>
              <ArrowBackIcon className="pointer" fontSize="large" onClick={handleNavigation} />
              <HeadlineMedium marginRight="auto">Detalhes do pagamento</HeadlineMedium>
            </MainTitle>
          </Header>
          <PersonalInformation />
          <ProposalInformation />
          <InstallmentArea>
            <BodySmallBold>Parcelas pagas</BodySmallBold>
            <BodySmallRegular>Confira abaixo as parcelas que já foram pagas.</BodySmallRegular>
          </InstallmentArea>
          <InstallmentTable />
          <InstallmentArea>
            <BodySmallBold>Parcelas em aberto</BodySmallBold>
            <BodySmallRegular>
              Confira abaixo as parcelas em atraso ou a vencer. Selecione as que deseja pagar.
            </BodySmallRegular>
            <BodySmallBoldStyled>
              Importante: O celular só será desbloqueado se todas as parcelas em atraso forem quitadas.
            </BodySmallBoldStyled>
          </InstallmentArea>
          <InstallmentToPayTable />
        </Content>
      </Container>
    </CommonTemplate>
  );
};
